import { Injectable, Component, OnInit, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { filter, map, catchError, finalize} from 'rxjs/operators';
import {BehaviorSubject, from, Observable, throwError} from 'rxjs';
import {MenuItem} from '../Models/menuItem';
import {JwtHelperService} from '@auth0/angular-jwt';
import {AppConfig} from '../app.config';
import {BsModalService} from 'ngx-bootstrap/modal';
import {Router} from '@angular/router';

@Injectable({ providedIn: 'root'})
export class AuthenticationService {

  private loggedIn = new BehaviorSubject<boolean>(false);

  private  menuItems = new BehaviorSubject<MenuItem[]>(null);

  errorMessage = '';

  get isLoggedIn() {
    return this.loggedIn.asObservable();
  }

  get getMenus() {
    return this.menuItems.asObservable();
  }

  constructor(private http: HttpClient, private appConfig: AppConfig,
              private jwtHelper: JwtHelperService, private modalService: BsModalService, private router: Router) { }

  login(username: string, password: string) {

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
     });

    return this.http.post<any>('api/login', { username, password },{headers: headers, withCredentials: true})
      .pipe(
        /* Catch a backend error and let the component know */
        catchError( err => {
          /* Rethrow error */
          return throwError( err );
        }),
        map(user => {
          // login successful if there's a jwt token in the response
               if (user && user.token) {
                 if (!user.isTempPassword) {
                   // store user details and jwt token in local storage to keep user logged in between page refreshes
                   localStorage.setItem('userLogon', username);
                   localStorage.setItem('TokenInfo', user.token);
                   localStorage.setItem('userId', user.userId);
                   localStorage.setItem('refreshToken', user.refreshToken);

                   this.loggedIn.next(true);
                   this.getMenuItems();
                 }
               } else {
                 this.loggedIn.next(false);
               }
               return user;
             }),
      );
  }

  logout() {
    return this.http.get<void>('/api/logout')
      .pipe(
        map(() => {
          // remove user from local storage to log user out
          localStorage.removeItem('userLogon');
          localStorage.removeItem('userId');
          localStorage.removeItem('TokenInfo');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('navMenu');
          localStorage.removeItem('favPage');
          this.loggedIn.next(false);
        })
      );
  }

  private getMenuItems() {
    this.errorMessage = '';
    this.http.get<MenuItem[]>('/api/userMenuItems').subscribe(result => {
      this.menuItems.next(result);
    }, error => { console.error(error); this.errorMessage = error; }) ;

  }

  public async tryRefreshingTokens(token: string): Promise<boolean> {
    // Try refreshing tokens using refresh token
     if (!token) {
       return false;
     }

    const refreshToken: string = localStorage.getItem('refreshToken');
    const credentials = { token: token, refreshToken: refreshToken };
    let isRefreshSuccess: boolean;
    try {
      const response = await this.http.post('/api/token/refresh', credentials, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json'
        }),
        observe: 'response'
      }).toPromise();

      // If token refresh is successful, set new tokens in local storage.
      const newToken = (<any>response).body.token;
      const newRefreshToken = (<any>response).body.refreshToken;
      localStorage.setItem('TokenInfo', newToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      isRefreshSuccess = true;
    }  catch (ex) {
      isRefreshSuccess = false;
    }

    this.loggedIn.next(isRefreshSuccess);
    return isRefreshSuccess;
  }

  checkAuthModal() {
    const token = localStorage.getItem('TokenInfo');

    if (token && this.jwtHelper.isTokenExpired(token)) {
      setTimeout(() => {
        this.forceCloseModal();
        this.router.navigate(['/login']);
      }, 800);
      return false;
    }
    return true;
  }

  forceCloseModal() {
    if (this.modalService.getModalsCount() > 0) {
      document.body.className = document.body.className.replace('modal-open', '');
    }

    for (let i = 1; i <= this.modalService.getModalsCount(); i++) {
      this.modalService.hide(i);
    }
  }

  getUserSecurityQuestions(login, token) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
    });

    return this.http.post<any[]>('api/securityQuestions/pageLoad', { username: login, token: token },{headers: headers})
      .pipe(
        /* Catch a backend error and let the component know */
        catchError( err => {
          /* Rethrow error */
          return throwError( err );
        }),
        map(user => {
          return user;
        }),
      );
  }

  saveQuestions(answers: any[], userName, loginForgotPass, questions: any[], token) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
    });
    return this.http.post<any[]>('api/securityQuestions/save', { answers, userName, loginForgotPass, questions, token }, {headers: headers})
      .pipe(
        /* Catch a backend error and let the component know */
        catchError( err => {
          /* Rethrow error */
          return throwError( err );
        }),
        map(user => {
          return user;
        }),
      );
  }

  saveTempPassword(loginHandleQuiz, username, currentPassword, newPassword, confirmNewPassword) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET, POST, PATCH, PUT, DELETE, OPTIONS',
      'Access-Control-Allow-Headers': 'Origin, Content-Type, X-Auth-Token',
    });
    return this.http.post<any[]>('api/changePassword/saveTempPassword',
      { tokenQuiz: loginHandleQuiz, username, currentPassword, newPassword, confirmNewPassword }, { headers });
  }

}
